/**
 * Integrytas Group - Global Trash Can & Deep Undo (GTC)
 * Page: GTC.TRASH
 *
 * Workspace-segregated trash. Each entry is one delete operation (root record +
 * its cascade hierarchy). "Restore" performs a Deep Undo — clearing the soft-
 * delete flag across the whole tree atomically. Items auto-purge after the
 * configured window.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { useActiveWorkspace } from '../../lib/use_workspace';
import { useFormatDate } from '../../lib/format_date';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Search, RefreshCw } from 'lucide-react';
import { hasAccess, type AuthSession } from '../../types/auth';

interface LedgerRow {
  gtc_ledger_id: number; root_entity: string; root_pk_value: string; label: string | null;
  affected_count: number; deleted_at: string; expires_at: string | null; dependency_tree: unknown;
  une_workspace_id: number | null;
}
interface ManagedRow { entity_name: string; pk_column: string }

export interface GtcTrashPageProps { session: AuthSession }

export function GtcTrashPage({ session }: GtcTrashPageProps) {
  return (
    <PersonalizationProvider pageCode="GTC.TRASH" session={session}>
      <GtcTrashPageInner session={session} />
    </PersonalizationProvider>
  );
}


const PAGE_CODE = 'GTC.TRASH';
function GtcTrashPageInner({ session }: GtcTrashPageProps) {
  // §6/§17: the write below is page-scoped, so the affordance follows
  // csm_role_page_access. The server refuses it regardless; this stops the
  // page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const LEDGER_GRID_COLS: GridColumnDef[] = [
    { key: 'item',       header: t('gtc.col.item', 'Item'),             defaultWidth: 280 },
    { key: 'affected',   header: t('gtc.col.affected', 'Affected'),     defaultWidth: 120 },
    { key: 'deleted',    header: t('gtc.col.deleted', 'Deleted'),       defaultWidth: 180 },
    { key: 'auto_purge', header: t('gtc.col.auto_purge', 'Auto-purge'), defaultWidth: 140 },
  ];
  const { workspaces, activeCode, activeId, setActive } = useActiveWorkspace();
  const [rows, setRows] = useState<LedgerRow[]>([]);
  const [managed, setManaged] = useState<ManagedRow[]>([]);
  const [open, setOpen] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // §17.B13 related-children disclosure. The cascade hierarchy is an untyped JSON
  // blob shown via the existing "Tree" toggle, so this control renders inert.
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => []; // TODO: wire relations
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facet on the entity type (workspace has its own selector).
  const [facets, setFacets] = useState<{ entity: string[] }>({ entity: [] });

  const reload = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const [l, m] = await Promise.all([
        apiDb.from('gtc_ledger')
          .select('gtc_ledger_id, root_entity, root_pk_value, label, affected_count, deleted_at, expires_at, dependency_tree, une_workspace_id')
          .eq('status', 'TRASHED').order('deleted_at', { ascending: false }).limit(200),
        apiDb.from('gtc_managed_entity').select('entity_name, pk_column').eq('is_active', 'Y').order('entity_name'),
      ]);
      if (l.error) throw l.error;
      setRows(((l.data ?? []) as LedgerRow[]).filter(r => activeId == null || r.une_workspace_id === activeId));
      setManaged((m.data ?? []) as ManagedRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }, [activeId]);
  useEffect(() => { void reload(); }, [reload]);

  // Distinct facet options derived from the loaded rows (§17.B5).
  const entityOptions = useMemo(
    () => [...new Set(rows.map(r => r.root_entity).filter(Boolean))].sort().map(v => ({ value: v, label: v })),
    [rows],
  );
  const anyFilterActive = facets.entity.length > 0 || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ entity: [] }); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // A row passes the facet when no values are selected, or it matches one.
    const matchesFacets = (r: LedgerRow) => facets.entity.length === 0 || facets.entity.includes(r.root_entity);
    return rows.filter(r =>
      matchesFacets(r) &&
      (!q ||
        (r.label ?? '').toLowerCase().includes(q) ||
        r.root_entity.toLowerCase().includes(q) ||
        r.root_pk_value.toLowerCase().includes(q)));
  }, [rows, filter, facets]);

  const restore = async (id: number) => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const { data, error: e } = await apiDb.rpc('gtc_restore', { p_ledger_id: id });
      if (e) throw e;
      const r = data as { ok: boolean; restored?: number; error_code?: string };
      if (!r?.ok) throw new Error(r?.error_code ?? 'GTC.RESTORE_FAILED');
      setNotice(t('gtc.restored', 'Restored {{count}} record(s).', { count: r.restored }));
      await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('gtc.title', 'Trash Can')}
          description={t('gtc.list.description', 'Soft-deleted records by workspace — restore an item and its whole cascade, or let it auto-purge.')}
          actions={
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> {t('gtc.refresh', 'Refresh')}
            </button>
          }
        />
        {error && (
          <ErrorBanner
            message={t('gtc.error_banner', "Couldn't complete the last trash-can operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="w-48">
              <FilteredModalSingleSelect
                label={t('gtc.workspace', 'Workspace')}
                options={workspaces.map(w => ({ internal_code: w.internal_code, display_text: w.name }))}
                selectedCode={activeCode || null}
                onChange={(code) => setActive(code ?? activeCode)}
                allowClear={false}
              />
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('gtc.filter_ph', 'Filter items…')}
                className="h-8 w-60 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('gtc.col.entity', 'Entity')} options={entityOptions} selected={facets.entity} onChange={v => setFacets(f => ({ ...f, entity: v }))} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-watermark">{t('gtc.working', 'Working…')}</span>}
              {notice && <span className="text-csm-xs text-primary">{notice}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={LEDGER_GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1" title={t('gtc.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('gtc.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.gtc_ledger_id}>
                    <tr className="hover:bg-csm-surface-alt">
                      <td className="border-b border-csm-border-soft px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                        <RelatedExpandButton open={detail.isOpen(r.gtc_ledger_id)} hasChildren={hasRelated(r.gtc_ledger_id)} onToggle={() => detail.toggle(r.gtc_ledger_id)} expandLabel={t('gtc.related.expand', 'Show related records')} collapseLabel={t('gtc.related.collapse', 'Hide related')} tooltip={t('gtc.related.tooltip', 'Show related records')} />
                      </td>
                      {visibleCols.map(col => (
                        <td key={col.key} className="border-b border-csm-border-soft px-2 py-1"
                            style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                          {col.key === 'item'       && <span className="font-mono text-csm-sm">{r.label ?? `${r.root_entity} #${r.root_pk_value}`}</span>}
                          {col.key === 'affected'   && <span className="text-csm-xs">{t('gtc.records', '{{count}} record(s)', { count: r.affected_count })}</span>}
                          {col.key === 'deleted'    && <span className="text-csm-2xs text-csm-watermark">{new Date(r.deleted_at).toLocaleString()}</span>}
                          {col.key === 'auto_purge' && <span className="text-csm-2xs text-csm-watermark">{r.expires_at ? formatDate(r.expires_at) : '—'}</span>}
                        </td>
                      ))}
                      <td className="border-b border-csm-border-soft px-2 py-1 text-right">
                        <button onClick={() => setOpen(open === r.gtc_ledger_id ? null : r.gtc_ledger_id)} className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{open === r.gtc_ledger_id ? t('gtc.hide', 'Hide') : t('gtc.tree', 'Tree')}</button>
                        {canChange && (<button onClick={() => void restore(r.gtc_ledger_id)} disabled={busy} className="rounded border border-primary bg-primary px-2 py-0.5 text-csm-xs text-white hover:bg-primary-hover disabled:opacity-60">{t('gtc.restore', 'Restore')}</button>)}
                      </td>
                    </tr>
                    {open === r.gtc_ledger_id && (
                      <tr>
                        <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt px-2 py-1">
                          <pre className="max-h-64 overflow-auto font-mono text-csm-2xs text-csm-ink-soft">{JSON.stringify(r.dependency_tree, null, 2)}</pre>
                        </td>
                      </tr>
                    )}
                    {detail.isOpen(r.gtc_ledger_id) && (
                      <tr>
                        <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                          <RelatedChildrenPanel groups={buildRelatedGroups(r.gtc_ledger_id)} session={session} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && <tr><td colSpan={visibleCols.length + 2} className="px-2 py-8 text-center">
                  <EmptyState variant={anyFilterActive ? 'no-match' : 'all-clear'} title={t('gtc.empty', 'Trash is empty for this workspace.')} />
                </td></tr>}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>

        <div className="shrink-0 rounded border border-csm-border bg-white p-3">
          <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('gtc.managed_entities', 'Managed Entities')}</div>
          {managed.length === 0
            ? <div className="text-csm-2xs text-csm-watermark">{t('gtc.no_managed_pre', 'No tables are trash-managed yet. A DBA enables one with')} <span className="font-mono">SELECT gtc_enable('entity','pk_column')</span>.</div>
            : <div className="flex flex-wrap gap-1">{managed.map(m => <span key={m.entity_name} className="rounded bg-csm-surface-alt px-1.5 py-0.5 font-mono text-csm-2xs text-csm-ink-soft">{m.entity_name}</span>)}</div>}
        </div>
      </div>
    </div>
  );
}
